<template>
  <div class="product-block relative" v-on-clickaway="closeSelect">
    <div :class="hasResults ? 'active' : ''">
      <div class="relative">
        <InputText
          :label="label"
          v-model="inputSearch"
          :rules="{ required: true }"
          @keyup.native="onKeyup"
        />
      </div>
      <div class="search-result-dropdown" v-show="dropdownVisible">
        <div v-if="results.length > 0">
          <ul style="margin-bottom: 7px">
            <li
              v-for="(item, index) in results"
              :key="`desc-${index}`"
              :ref="`${index}-opt`"
              tabIndex="-1"
              class="list-item"
              @keydown.prevent="nextItemList"
              @click="selectDescription(item)"
            >
              <div class="product-description m-l-5">
                <div class="desc-title" v-html="highlight(displayText(item))"></div>
              </div>
            </li>
          </ul>
        </div>
        <div class="dropdown-footer">
          <template v-if="canSave && noExactMatch">
            <div class="dropdown-footer-center">
              <Btn
                class="brand"
                style="height: 41px"
                :icon="{
                  name: 'edit',
                  size: '18',
                }"
                :btnText="saving ? $t('LBL_SAVING') + '...' : $t('LBL_SAVE_TEXT', { text: inputTextTruncated })"
                :disabled="saving"
                @click.native="quickSave"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import InputText from '@/components/elements/inputs/text.vue';
import Btn from '@/components/elements/buttons/button.vue';
import { directive as onClickaway } from 'vue-clickaway';
import _ from 'lodash';
import { mapGetters } from 'vuex';

export default {
  name: 'descriptionPicker',
  components: { InputText, Btn: Btn },
  directives: { onClickaway },
  props: {
    label: { type: String, default: 'description' },
    value: { type: String, default: '' },
    minChars: { type: Number, default: 4 }, // API & button both start at 4 chars
  },
  data() {
    return {
      inputSearch: '',
      indexItem: 0,
      results: [],
      hasResults: false,
      dropdownVisible: false,
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['get_items_list', 'get_active_subaccount']),
    canSave() {
      const len = this.inputSearch ? this.inputSearch.trim().length : 0;
      return len >= this.minChars;
    },
    inputTextTruncated() {
      const text = this.inputSearch || '';

      if (text.length <= 13) {
        return text;
      }

      return text.slice(0, 13) + '...';
    },
    noExactMatch() {
      const needle = this.normalize(this.inputSearch);
      if (!needle) return false;
      return !this.results.some(r => this.normalize(this.displayText(r)) === needle);
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal !== this.inputSearch) {
          this.inputSearch = newVal || '';
        }
      },
      immediate: true
    },
    inputSearch(newVal) {
      this.$emit('input', newVal);
    },
  },
  mounted() {
    this.inputSearch = this.value;
  },
  methods: {
    normalize(s) {
      return String(s || '').trim().toLowerCase();
    },
    displayText(item){
      return item.description || item.name || '';
    },
    highlight(text) {
      if (!text) return '';
      const reggie = new RegExp(this.inputSearch, 'ig');
      const found = String(text).search(reggie) !== -1;
      return !found ? text : String(text).replace(reggie, `<b>${this.inputSearch}</b>`);
    },
    onKeyup: _.debounce(function(event) {
      if (event.keyCode === 9) return; // tab
      if (event.keyCode === 38 || event.keyCode === 40) {
        if (this.results.length > 0) {
          this.$refs[`${this.indexItem}-opt`][0].focus();
        }
        return;
      }

      const len = this.inputSearch ? this.inputSearch.trim().length : 0;
      this.dropdownVisible = len >= this.minChars;

      if (len >= this.minChars) {
        this.$store
          .dispatch('GET_ITEMS', { page: 1, query: `&type_id=2&searchTerm=${encodeURIComponent(this.inputSearch)}` })
          .then(() => {
            this.results = this.get_items_list || [];
            this.hasResults = this.results.length > 0;
          });
      } else {
        this.hasResults = false;
        this.dropdownVisible = false;
        this.results = [];
      }
    }, 220),
    nextItemList(event) {
      if (event.keyCode === 9) return;
      if (event.keyCode === 38 && this.indexItem > 0) {
        this.indexItem--;
        this.$refs[`${this.indexItem}-opt`][0].focus();
      } else if (event.keyCode === 40 && this.indexItem < this.results.length - 1) {
        this.indexItem++;
        this.$refs[`${this.indexItem}-opt`][0].focus();
      } else if (event.keyCode === 13) {
        this.$refs[`${this.indexItem}-opt`][0].click();
      }
    },
    selectDescription(item) {
      const text = this.displayText(item);
      this.$emit('input', text);
      this.inputSearch = text;
      this.dropdownVisible = false;
    },
    async quickSave() {
      if (!this.canSave || this.saving) return;
      this.saving = true;
      const desc = this.inputSearch.trim();
      try {
        const activeId = _.get(this, 'get_active_subaccount.id');
        const accessible = activeId ? [String(activeId)] : [];
        await this.$store.dispatch('ADD_ITEM', {
          type_id: 2,
          is_visible: 1,
          name: desc,
          description: desc,
          accessible_for_subaccounts: accessible,
        });
        this.$emit('input', desc);
        this.dropdownVisible = false;
      } catch (e) {
        // Fail silently, notification bar handled in store usually
      } finally {
        this.saving = false;
      }
    },
    closeSelect() {
      this.dropdownVisible = false;
    },
  },
};
</script>

<style scoped>
.search-result-dropdown {
  position: absolute;
  top: 68px;
  width: 400px;
  padding: 20px;
  box-shadow: 0px 0px 8px 0px #a0a0a0;
  background: white;
  z-index: 1000;
  border-left: 1px solid #DBDFE3;
  border-right: 1px solid #DBDFE3;
  border-radius: 0 0 4px 4px;
}

.list-item {
  display: flex;
  align-items: center;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease-in-out;
}

.list-item:hover {
  background-color: #f5f7fa; /* gray hover */
}

.list-item:last-child {
  border-bottom: none;
}

.no-results {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.muted {
  color: #777;
}

.desc-title {
  font-size: 14px;
}

.dropdown-footer {
/*  margin-top: 10px;*/
}

.dropdown-footer-center {
  display: flex;
  justify-content: center; /* centers the button (and its icon) */
}
</style>
